import type { Messages } from './messages';

export const en: Messages = {
  'lang.name': 'English',
  'lang.aria': 'Language',
  'app.title': 'Web synthesizer',

  // Header — mapping shift
  'header.shift.aria': 'Shift the keyboard mapping',
  'shift.octDown': '− 8ve',
  'shift.noteDown': '− note',
  'shift.noteUp': '+ note',
  'shift.octUp': '+ 8ve',
  'shift.octDown.title': 'One octave down (− key)',
  'shift.noteDown.title': 'One white note down (_ key)',
  'shift.noteUp.title': 'One white note up (+ key)',
  'shift.octUp.title': 'One octave up (= key)',

  // Install button
  'install.prompt': 'Install the app',
  'install.installed': 'Installed',
  'install.promptTitle': 'Install the synthesizer as an app',
  'install.uninstallHint':
    'The app is installed. Browsers do not allow uninstalling from a page; use the browser menu (⋮) → "Uninstall", or your system apps.',
  'install.manualHint':
    'If it does not appear automatically: from the browser menu (⋮) choose "Install app". On iPhone/iPad: Share → "Add to Home Screen".',

  // Metronome
  'metronome.label': 'Metronome',
  'metronome.tempoDown.aria': 'Slower tempo',
  'metronome.tempoUp.aria': 'Faster tempo',
  'metronome.bpm.aria': 'Tempo (BPM)',
  'metronome.tap': 'Tap',
  'metronome.tap.aria': 'Tap the tempo by clicking',
  'metronome.start': 'Start',
  'metronome.stop': 'Stop',
  'meter.aria': 'Meter (time signature)',
  'meter.minus.aria': 'Fewer beats',
  'meter.plus.aria': 'More beats',
  'meter.accents.aria': 'Beat accents',
  'meter.custom': 'Custom…',

  'sustain': 'Sustain',
  'reset.all': 'Reset all settings',

  // Section titles
  'section.keyboard': 'Keyboard',
  'section.engine': 'Sound engine',
  'section.sound': 'Instrument and sound',
  'section.effects': 'Effects',
  'section.curve': 'Level curve across the range (bass ↔ treble)',
  'section.offline': 'Offline availability',

  // Keyboard section
  'labels.aria': 'Key labels',
  'label.cde': 'CDE',
  'label.cde.title': 'Letters on the keys (C D E …)',
  'label.doremi': 'DoReMi',
  'label.doremi.title': 'Solfège on the keys (Do Re Mi …)',
  'label.keys': 'Keys',
  'label.keys.title': 'The keyboard key on each note',
  'label.octave': 'Octave',
  'label.octave.title': 'The octave number, at the top of the key',
  'field.startNote': 'Start note',
  'field.startOctave': 'Start octave',
  'field.octaveCount': 'Number of octaves',
  'field.displayMode': 'Display mode',
  'display.scroll': 'Horizontal scroll',
  'display.fit1': 'Scaled to one row',
  'display.fit2': 'Scaled to two rows',
  'range.label': 'Keyboard range',
  'range.keys': '{n} keys',
  'range.low.aria': 'Lowest note',
  'range.high.aria': 'Highest note',
  'range.drag.aria': 'Move the whole range',
  'octaveCount.full': 'Full range (88 keys)',
  'octaveCount.partial': '— (partial range)',

  // Engine section
  'field.engine': 'Engine',
  'engine.synth': 'Synthesized (Tone.js)',
  'engine.sampler': 'Real sample (recorded instruments)',
  'field.synthType': 'Synth type',
  'field.preset': 'Preset',
  'preset.aria': 'Instrument preset',
  'field.waveform': 'Timbre',
  'waveform.aria': 'Waveform (timbre)',
  'field.instrument': 'Instrument',
  'field.density': 'Sample density',
  'density.6': 'Fast (1 sample / 6 semitones)',
  'density.3': 'Balanced (1 sample / 3 semitones)',
  'density.1': 'Full (all samples)',

  // Sampler status
  'status.loading': 'Loading "{instrument}": {loaded} / {total} samples…',
  'status.upgrading': 'Playable now ({count} samples) · loading full quality…',
  'status.ready': 'Ready: {count} samples ({low}–{high}){failed}. Notes in between are transposed.',
  'status.readyFailed': ' · {failed} failed',
  'status.cached': 'Instrument loaded (from cache).',
  'status.errNoSamples': 'This instrument has no samples in the library.',
  'status.errLoadFailed': 'Could not load any sample (check your connection).',

  // Keyboard loading overlay
  'overlay.loading': 'Loading "{instrument}"… {loaded} / {total}',
  'overlay.preparing': 'Preparing the instrument…',
  'overlay.error': 'Could not load the samples: {message}',

  // Sound knobs
  'knob.attack': 'Attack (s)',
  'knob.release': 'Release (s)',
  'knob.volume': 'Volume',
  'knob.filter': 'Brightness',
  'knob.filter.aria': 'Brightness (low-pass filter)',
  'knob.echo': 'Echo',
  'knob.reverb': 'Reverb',
  'resetInstrument': 'Reset to default',
  'resetInstrument.title': 'Return this instrument’s sound to its default values',

  // Filter tone words
  'filter.veryDark': 'very dark',
  'filter.dark': 'dark',
  'filter.neutral': 'neutral',
  'filter.bright': 'bright',
  'filter.veryBright': 'very bright',

  // Register curve
  'curve.hint':
    'Perceived-loudness compensation for the synthesized oscillators, which are technically flat but sound thin in the bass. Recorded instruments bring their own balance (measured flat within ~2.7 dB from C1 to C7), so by default the curve is not applied to them.',
  'curve.bassPre': 'Bass:',
  'curve.bassPost': 'dB / octave below C4',
  'curve.treblePre': 'Treble:',
  'curve.treblePost': 'dB / octave above C4',
  'curve.onSamples': 'Apply to real samples too',
  'curve.notApplied': 'The curve is not applied to the sample engine (tick the box to apply it).',

  // Offline section
  'offline.hint':
    'Install the app from the browser menu ("Install" / "Add to Home Screen"), then download all the real samples to the cache. After that the synthesizer works fully offline, on any device. The download is on the order of ~100–250 MB and happens only once.',
  'offline.download': 'Download everything for offline',
  'offline.refresh': 'Refresh the offline cache',
  'offline.done': 'All samples have been downloaded — available offline.',
  'offline.downloading': 'Downloading {done} / {total} samples…{failed}',
  'offline.failedSuffix': ' ({failed} failed)',
  'offline.doneAll': 'Done — all {total} samples are available offline.',
  'offline.doneWithErrors': 'Downloaded with {failed} errors — retry to complete the cache.',

  // Engine notices
  'engine.fallback': '"{label}" could not be created ({reason}). Fell back to Synth.',
  'worklet.hint':
    'The page is opened directly from disk (file://), where the browser refuses AudioWorklet modules: PluckSynth and BitCrusher cannot work. Run "npm run dev" and open the http address shown there for them to work.',

  // Keyboard area
  'keyboard.aria': 'Keyboard',
  'keyboard.resize.aria': 'Adjust the keyboard height (drag, or up/down arrows; double-click = automatic)',
  'keyboard.resize.title': 'Drag for height · double-click = automatic',

  // Presets
  'preset.piano': 'Piano',
  'preset.organ': 'Organ',
  'preset.strings': 'Strings',
  'preset.bass': 'Bass',
  'preset.lead': 'Lead',
  'preset.custom': 'Custom',

  // Synth types
  'synth.synth': 'Synth',
  'synth.monosynth': 'MonoSynth',
  'synth.amsynth': 'AMSynth',
  'synth.fmsynth': 'FMSynth',
  'synth.duosynth': 'DuoSynth',
  'synth.membrane': 'MembraneSynth',
  'synth.metal': 'MetalSynth',
  'synth.noise': 'NoiseSynth (monophonic)',
  'synth.pluck': 'PluckSynth (monophonic)',

  // Waveforms
  'waveform.sine': 'Sine',
  'waveform.triangle': 'Triangle',
  'waveform.sawtooth': 'Sawtooth',
  'waveform.square': 'Square',

  // Effects
  'effect.mix': 'Mix',
  'effect.chorus': 'Chorus',
  'effect.distortion': 'Distortion',
  'effect.bitcrusher': 'BitCrusher',
  'effect.vibrato': 'Vibrato',
  'effect.autowah': 'AutoWah',
  'effect.phaser': 'Phaser',
  'effect.pingpong': 'Ping-pong delay',
  'effect.stereowidener': 'Stereo widener',
  'effect.eq3': '3-band EQ',
  'effect.pitchshift': 'Pitch shift',

  // Parameters (shared by effects and synths, keyed by param key)
  'param.frequency': 'Rate (Hz)',
  'param.distortion': 'Amount',
  'param.bits': 'Bits',
  'param.depth': 'Depth',
  'param.octaves': 'Octaves',
  'param.delayTime': 'Time (s)',
  'param.width': 'Width',
  'param.low': 'Low (dB)',
  'param.mid': 'Mid (dB)',
  'param.high': 'High (dB)',
  'param.pitch': 'Semitones',
  'param.harmonicity': 'Harmonicity',
  'param.modulationIndex': 'Modulation index',
  'param.vibratoAmount': 'Vibrato',
  'param.pitchDecay': 'Pitch decay (s)',
  'param.dampening': 'Damping (Hz)',
  'param.resonance': 'Resonance',
  'param.type': 'Noise color',

  // Instruments
  'instrument.piano': 'Piano',
  'instrument.guitar-acoustic': 'Acoustic guitar',
  'instrument.guitar-nylon': 'Nylon-string guitar',
  'instrument.guitar-electric': 'Electric guitar',
  'instrument.bass-electric': 'Electric bass',
  'instrument.bassoon': 'Bassoon',
  'instrument.cello': 'Cello',
  'instrument.clarinet': 'Clarinet',
  'instrument.contrabass': 'Double bass',
  'instrument.flute': 'Flute',
  'instrument.french-horn': 'French horn',
  'instrument.harmonium': 'Harmonium',
  'instrument.harp': 'Harp',
  'instrument.organ': 'Organ',
  'instrument.saxophone': 'Saxophone',
  'instrument.trombone': 'Trombone',
  'instrument.trumpet': 'Trumpet',
  'instrument.tuba': 'Tuba',
  'instrument.violin': 'Violin',
  'instrument.xylophone': 'Xylophone',

  // Meters
  'meter.4/4': '4/4 (common)',
  'meter.3/4': '3/4 (waltz)',
  'meter.2/4': '2/4',
  'meter.6/8': '6/8 (3+3)',
  'meter.5/4': '5/4 (Take Five · 3+2)',
  'meter.7/8': '7/8 (2+2+3)',
  'meter.9/8': '9/8 (Balkan · 2+2+2+3)',
};
